import { useCallback, useState } from "react";
import { OAuthServiceType } from "@common/constants";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useIntegrationConnectionFixingMutation } from "@api/integrationConnection.api";

export interface ConnectionFix {
    startFix: () => void;
    isFixing: boolean;
}

export function useOAuthConnectionFix({
    scope,
    onCode,
}: {
    scope: OAuthServiceType;
    onCode: (code: string) => void;
}): Pick<ConnectionFix, "startFix"> {
    const [isAuthorizing, setIsAuthorizing] = useState(false);

    useWindowMessage<OAuthAuthorizationResponse>({
        scope,
        handler: ({ code }) => {
            if (isAuthorizing) {
                onCode(code);
            }
            setIsAuthorizing(false);
        },
    });

    const startFix = useCallback(() => {
        triggerAuthorization(scope);
        setIsAuthorizing(true);
    }, [scope]);

    return { startFix };
}

export function useIntegrationOAuthConnectionFix({
    connection,
    scope,
    onFixed,
}: {
    connection: IntegrationConnection;
    scope: OAuthServiceType;
    onFixed?: () => void;
}): ConnectionFix {
    const fixConnectionMutation = useIntegrationConnectionFixingMutation(
        connection,
        onFixed,
    );
    const { startFix } = useOAuthConnectionFix({
        scope,
        onCode: (code) => fixConnectionMutation.mutate({ oAuthCode: code }),
    });

    return { startFix, isFixing: fixConnectionMutation.isPending };
}
